import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { Helmet } from 'react-helmet';
import Footer from '../../components/layout/Footer';
import { motion } from 'framer-motion';
import Button from '../../components/ui/Button';

// Background image for the page
const backgroundImage = 'https://images.unsplash.com/photo-1582582429416-77a96c0f4d9d?q=80&w=2070&auto=format&fit=crop';

const PageContainer = styled(motion.div)`
  background-color: #000;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const HeroSection = styled(motion.div)`
  background-color: #000;
  height: 20vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  position: relative;
  text-align: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.4);
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  width: 100%;
  padding: 0 2rem;
`;

 

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  font-style: italic;
  text-align: center;
  color: #0d9488;
`;

const Description = styled.p`
  font-size: 1.2rem;
  line-height: 1.7;
  margin-bottom: 2rem;
  text-align: center;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  color: #e0e0e0;
`;

const Highlight = styled.span`
  font-style: italic;
  color: #0d9488;
  font-weight: 600;
`;

const ContentSection = styled.div`
  flex: 1;
  width: 100%;
  padding: 4rem 0;
  background-image: linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)), url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  color: #fff;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const ServicesContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 3rem;
  margin: 4rem 0;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled.div`
  background-color: #1a1a1a;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  }
`;

const CardImage = styled.img`
  width: 100%;
  height: 350px;
  object-fit: cover;
`;

const CardContent = styled.div`
  padding: 2rem;
  text-align: center;
`;

const CardTitle = styled.h3`
  font-size: 1.8rem;
  margin-bottom: 1rem;
  color: #0d9488;
  text-align: center;
  font-style: italic;
`;

const CardButton = styled(motion.button)`
  background-color: #0d9488;
  color: white;
  border: none;
  border-radius: 50px;
  padding: 0.8rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 1rem;
  transition: background-color 0.3s ease;
  text-transform: uppercase;
  
  &:hover {
    background-color: #0f766e;
  }
`;

const CTASection = styled.div`
  margin-top: 6rem;
  text-align: center;
`;

const Laundry: React.FC = () => {
  const { language } = useLanguage();
  
  // Animation variants
  const pageVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const heroVariants = {
    initial: { y: -50, opacity: 0 },
    animate: { y: 0, opacity: 1, transition: { duration: 0.6 } }
  };
  
  const buttonVariants = {
    hover: { 
      scale: 1.05,
      backgroundColor: '#0f766e',
      boxShadow: '0 8px 20px rgba(0, 0, 0, 0.15)',
      y: -5
    },
    tap: { scale: 0.95 }
  };
  
  return (
    <PageContainer
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
    >
      <Helmet>
        <title>{language === 'it' ? 'Lavanderia | J-O-H' : 'Laundry | J-O-H'}</title>
        <meta 
          name="description" 
          content={language === 'it' 
            ? 'Servizio di lavanderia completo, noleggio biancheria e pulizia per proprietari di immobili in affitto breve' 
            : 'Complete laundry service, linen rental and cleaning for short-term rental property owners'} 
        />
      </Helmet>
      
      <HeroSection variants={heroVariants}>
        <HeroContent>
          <PageTitle>LAVANDERIA</PageTitle>
        </HeroContent>
      </HeroSection>
      
      <ContentSection>
        <ContentContainer>
          <Description>
            {language === 'it' 
              ? <><Highlight>JOLS</Highlight> (Join Our Laundry Services) è il servizio di lavanderia completo, noleggio biancheria + pulizia, cucito sulle esigenze di ogni proprietario, host, property manager o immobile.</>
              : <><Highlight>JOLS</Highlight> (Join Our Laundry Services) is the complete laundry service, linen rental + cleaning, tailored to the needs of each owner, host, property manager or property.</>}
          </Description>
          
          <ServicesContainer>
            <ServiceCard>
              <CardImage src="https://images.unsplash.com/photo-1604335399105-a0c585fd81a1?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80" alt="Noleggio Biancheria" />
              <CardContent>
                <CardTitle>{language === 'it' ? 'NOLEGGIO BIANCHERIA' : 'LINEN RENTAL'}</CardTitle>
                <Link to="/services/linen-rental">
                  <CardButton
                    whileHover="hover"
                    whileTap="tap"
                    variants={buttonVariants}
                  >
                    {language === 'it' ? 'Scopri' : 'Discover'}
                  </CardButton>
                </Link>
              </CardContent>
            </ServiceCard>
            
            <ServiceCard>
              <CardImage src="https://images.unsplash.com/photo-1527515637462-cff94eecc1ac?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2074&q=80" alt="Pulizia" />
              <CardContent>
                <CardTitle>{language === 'it' ? 'PULIZIA' : 'CLEANING'}</CardTitle>
                <Link to="/services/cleaning">
                  <CardButton
                    whileHover="hover"
                    whileTap="tap"
                    variants={buttonVariants}
                  >
                    {language === 'it' ? 'Scopri' : 'Discover'}
                  </CardButton>
                </Link>
              </CardContent>
            </ServiceCard>
          </ServicesContainer>
          
          <CTASection>
            <Button variant="primary" to="/contact">
              {language === 'it' ? 'Contattaci per maggiori informazioni' : 'Contact us for more information'}
            </Button>
          </CTASection>
        </ContentContainer>
      </ContentSection>
      
      <Footer />
    </PageContainer>
  );
};

export default Laundry; 